import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  SecondaryButton,
} from 'components/FormModal';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
`;

const DetailValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

const CreditAmount = styled(DetailValue)`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-size: 24px;
`;

const StyledModalFooter = styled(ModalFooter)`
  justify-content: flex-end;
`;

/**
 * CreditDetailModal - A modal to display credit details
 */
const CreditDetailModal = ({ isOpen, onClose, credit }) => {
  const [modalRoot, setModalRoot] = useState(null);

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !modalRoot || !credit) return null;

  // Format the date
  const formattedDate = credit.date
    ? moment(credit.date, 'MM-DD-YYYY').format('ddd MMM D - YYYY').toUpperCase()
    : '-';

  // Format the expiration date
  const formattedExpirationDate = credit.expiration
    ? moment(credit.expiration, 'MM-DD-YYYY')
        .format('ddd MMM D - YYYY')
        .toUpperCase()
    : 'No Expiration';

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="dialog"
          aria-labelledby="credit-detail-modal-title"
          aria-modal="true"
          maxWidth="520px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose} aria-label="Close modal">
            <CloseIcon />
          </CloseButton>

          <ModalHeader>
            <ModalTitle id="credit-detail-modal-title">
              Credit Details
            </ModalTitle>
          </ModalHeader>

          <ModalContent>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Credit Amount</DetailLabel>
                <CreditAmount>{credit.amount || '-'}</CreditAmount>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Credit #</DetailLabel>
                <DetailValue>{credit.id}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Issued Date</DetailLabel>
                <DetailValue>{formattedDate}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Expiration Date</DetailLabel>
                <DetailValue>{formattedExpirationDate}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Issued By</DetailLabel>
                <DetailValue>
                  {credit.issued_by?.formatted_name || '-'}
                </DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Original Total</DetailLabel>
                <DetailValue>{credit.original_amount || '-'}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Track Credit</DetailLabel>
                <DetailValue>{credit.track_credit || '-'}</DetailValue>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Fee Credit</DetailLabel>
                <DetailValue>{credit.fee_credit || '-'}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </ModalContent>

          <StyledModalFooter>
            <SecondaryButton type="button" onClick={onClose}>
              Close
            </SecondaryButton>
          </StyledModalFooter>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

CreditDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  credit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.string,
    date: PropTypes.string,
    expiration: PropTypes.string,
    issued_by: PropTypes.shape({
      formatted_name: PropTypes.string,
    }),
    original_amount: PropTypes.string,
    track_credit: PropTypes.string,
    fee_credit: PropTypes.string,
  }),
};

export default CreditDetailModal;
